import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// Libraries
import { Observable, Subject } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { concatMap, takeUntil } from 'rxjs/operators';

// Services
import { AuthService } from '@auth/auth.service';
import { CustomerRecurrenceService, CustomerRecurrenceType } from '@shared/services';

// Enums
import { COOKIE_ENUMERATION } from '@home/login/configs';

// Models
import { CustomResponseDto } from '@core/model/custom-response-dto';

// Environments
import { environment } from '@environments/environment';

@Component({
  selector: 'sso',
  template: `
    <h3 id="msjErrorSso" *ngIf="error" style="padding: 30px">
      Hubo un error, te redireccionaremos para que lo intentes nuevamente
    </h3>
  `,
})
export class SsoComponent implements OnInit, OnDestroy {
  public error: boolean;
  private unSubscribe$: Subject<void>;

  constructor(
    private readonly authService: AuthService,
    private readonly customerRecurrenceService: CustomerRecurrenceService,
    private readonly activateRoute: ActivatedRoute,
    private readonly cookieService: CookieService,
    private readonly statusService: CustomerRecurrenceService,
    private readonly router: Router,
    private readonly http: HttpClient
  ) {
    this.error = false;
    this.unSubscribe$ = new Subject();
  }

  public ngOnInit(): void {
    this.startAuthenticationProcess();
  }

  public ngOnDestroy(): void {
    this.unSubscribe$.complete();
    this.unSubscribe$.unsubscribe();
  }

  private startAuthenticationProcess(): void {
    const { origin, redirect } = this.activateRoute.snapshot.queryParams;
    const accessToken: string = this.cookieService.get(COOKIE_ENUMERATION.CRFRAP);
    const tokenData = this.authService.getTokenData(accessToken);
    accessToken && tokenData
      ? this.ensureThatTheTokenIsValidAndVerifyNewOrReturningUser({ accessToken, origin, redirect })
      : this.unauthorizedDueToLackOfToken(origin);
  }

  /**
   * @description To authenticate the user,
   * it is validated that the token is valid and issued by rapicredit,
   * or for this an endpoint is assumed that executes a call in the AWS api-gateway,
   * additionally it is verified if the user is new or recurring through another of two service endpoint,
   * the logic channels are in the CustomerRecurrenceService class
   * @date 16/01/2024
   * @private
   * @param {{
   *     accessToken: string;
   *     redirect: string;
   *     origin: string;
   *   }} {
   *     accessToken,
   *     redirect,
   *     origin,
   *   }
   */
  private ensureThatTheTokenIsValidAndVerifyNewOrReturningUser({
    accessToken,
    redirect,
    origin,
  }: {
    accessToken: string;
    redirect: string;
    origin: string;
  }): void {
    this.ensureItIsAValidRapicreditToken(accessToken)
      .pipe(
        concatMap(
          (): Observable<CustomResponseDto<CustomerRecurrenceType>> =>
            this.customerRecurrenceService.getRecurrence$(accessToken)
        ),
        takeUntil(this.unSubscribe$)
      )
      .subscribe({
        next: (recurrenceResponse: CustomResponseDto<CustomerRecurrenceType>): void =>
          this.stepsToAuthTheUser({ accessToken, recurrenceResponse, redirect }),
        error: (error: HttpErrorResponse): void => this.ensureHandlingOfExceptions(error, origin),
      });
  }

  private ensureHandlingOfExceptions(error: HttpErrorResponse, origin: string) {
    error.status === 401 ? this.router.navigateByUrl('/login') : this.ensureHandlingOfErrorsInTheAuthProcess(origin);
  }

  private ensureHandlingOfErrorsInTheAuthProcess(origin: string): void {
    this.error = true;
    setTimeout((): void => this.unauthorizedDueToLackOfToken(origin), 3000);
  }

  /**
   * @description This function executes the necessary
   * logic to successfully authenticate the user and not affect other processes.
   * @date 16/01/2024
   * @private
   * @param {{
   *     accessToken: string,
   *     recurrenceResponse: CustomResponseDto<CustomerRecurrenceType>,
   *     redirect: string;
   *   }} { accessToken, recurrenceResponse, redirect }
   */
  private stepsToAuthTheUser({
    accessToken,
    recurrenceResponse,
    redirect,
  }: {
    accessToken: string;
    recurrenceResponse: CustomResponseDto<CustomerRecurrenceType>;
    redirect: string;
  }): void {
    this.authService.saveUser(accessToken);
    this.authService.saveToken(accessToken);
    this.statusService.saveRecurrenceStatus(recurrenceResponse.response);
    redirect ? this.router.navigateByUrl(redirect) : this.router.navigateByUrl('/home');
  }

  private unauthorizedDueToLackOfToken(originRoute: string): void {
    originRoute ? this.windowRedirect(originRoute) : this.router.navigateByUrl('/login');
  }

  private windowRedirect(originRoute: string) {
    window.location.href = originRoute;
  }

  /**
   * @description This endpoint belongs to an aws lambda running on the api-gateway
   * @date 16/01/2024
   * @private
   * @param {string} token
   * @return {*}  {Observable<void>}
   */
  private ensureItIsAValidRapicreditToken(token: string): Observable<void> {
    return this.http.get<void>(`${environment.apiGatewayUrl}auth/validate-token`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }
}
